import { useEffect, useState } from "react";
import { HDLModuleWASM } from "./sim/hdlwasm";
import { ExpectedSignal, useAnimationFrame } from "./simulation_options";
import Box from "@mui/joy/Box";
import ToggleButtonGroup from "@mui/joy/ToggleButtonGroup";
import Button from "@mui/joy/Button";

export function Switches({ switchCount, switches, setSwitches }: { switchCount: number, switches: number, setSwitches: (arg0: number) => void }) {
  // Cursedness: 87%. The ToggleButtonGroup looks pretty, but it works with strings,
  // one for each button, not an array of pressed buttons or anything. We'll work
  // around that by generating buttons with the correct 'value' string for each digit,
  // then translate between an array of strings like ["1", "3", "4"] and the binary repr
  // in this case 0b0101_1000
  if (switchCount) {
    return (
      <Box>
        <ToggleButtonGroup
          orientation={"vertical"}
          style={{ backgroundColor: "white", color: "black" }}
          value={[...Array(switchCount).keys()]
            .filter((num) => {
              return ((switches >> num) & 1) == 1
            })
            .map((num) => num.toString())
          }
          onChange={
            (_, newValue) => {
              let newValueInt = 0
              for (const digit of newValue) {
                newValueInt = newValueInt | (1 << parseInt(digit))
              }
              setSwitches(newValueInt)
            }
          }
        >
          {[...Array(switchCount).keys()].map((num) => {
            return <Button sx={{ width: "0.5em" }} value={num.toString()}>{num}</Button>
          })}
        </ToggleButtonGroup>
      </Box>
    )
  }
}

export const expectedTtSignals: ExpectedSignal[] =
  [
    { name: "ui_in", type: "uint<8>" },
    { name: "uo_out", type: "inv& uint<8>" },
    { name: "uio_in", type: "uint<8>" },
    { name: "uio_out", type: "inv& uint<8>" },
    { name: "uio_oe", type: "inv& uint<8>" },
    { name: "ena", type: "bool" },
    { name: "clk", type: "bool" },
    { name: "rst_n", type: "bool" }
  ]


export const TTInput = ({ hdlMod }: { hdlMod: HDLModuleWASM }) => {
  const [switches, setSwitches] = useState(0)

  useEffect(() => {
    if (hdlMod) {
      hdlMod.state.ui_in= switches;
    }
  }, [hdlMod, switches])

  return <Box
    padding="0.6em 0.8em"
    style={{ height: "fit-content" }}
    backgroundColor={"rgb(200, 75, 75)"}
    borderRadius="0.3em"
  >
    <Switches switches={switches} setSwitches={setSwitches} switchCount={8} />
  </Box>
}


export const TTOutput = ({ hdlMod }: { hdlMod: HDLModuleWASM }) => {
  const [outval, setOutVal] = useState(0);

  useEffect(() => {
    if (hdlMod) {
      hdlMod.state.rst_n = false
      hdlMod.tick()
      hdlMod.tick()
      hdlMod.state.rst_n = true
    }
  }, [hdlMod])

  useAnimationFrame((deltaTimeMs: number) => {
    if (hdlMod) {
      // Simulating a clock frequency of 10 KHz
      const ccsToSimulate = 10 * deltaTimeMs;
      for (let i = 0; i < ccsToSimulate; i++) {
        hdlMod.tick()
        hdlMod.tick()
      }

      setOutVal(hdlMod.state.uo_out)
    }
  })

  const segmentColor = (idx: number) => {
    if (((outval >> idx) & 1) == 1) {
      return "rgb(100, 255, 100)"
    } else {
      return "rgb(0, 50, 0)"
    }

  }
  const segments = (idx: number[]) => {
    return idx.map((idx) => {
      return segmentColor(idx)
    }).join(" ")
  }

  const SevenSeg = () => <Box>
    <Box style={{ width: "3.4em", padding: "0.3em", borderRadius: "0.2em", backgroundColor: "rgb(120, 120, 120)" }}>
      <Box
        width="2em"
        height="2em"
        style={{ borderWidth: "5px", borderStyle: "solid", borderColor: segments([0, 1, 6, 5]) }}
      ></Box>
      <Box style={{ display: "flex", flexDirection: "row" }}>
        <Box
          width="2em"
          height="1.7em"
          border="5px solid green"
          // borderStyle={"solid"}
          // borderWidth={"5px"}
          borderColor={"transparent " + segments([2, 3, 4])}
        ></Box>
        <Box
          width="0.5em"
          height="0.5em"
          borderRadius="0.3em"
          marginTop="1.2em"
          marginLeft="0.2em"
          style={{ backgroundColor: segmentColor(7) }}></Box>
      </Box>
    </Box>
    <Box height={"2em"}></Box>
  </Box>

  return <Box>
    <Box
      style={{
        backgroundColor: "rgb(55, 150, 100)",
        width: "fit-content",
        padding: "0.5em",
        margin: "1em",
        borderRadius: "0.5em"
      }}
    >
      <Box display="flex" flex-direction="row">
        <TTInput hdlMod={hdlMod} />
        <Box
          marginTop={"7em"}
          width={"14em"}
        >
          <Box width="100%">
            <Box style={{
              width: "4em",
              height: "4em",
              margin: "0px auto",
              backgroundColor: "rgb(75, 75, 75)",
              color: "rgb(150, 150, 150)"
            }}>
              <img src="https://tinytapeout.com/ttlogo.svg" />
            </Box>
          </Box>
        </Box>
        <Box display="flex" flexDirection="column">
          <Box height="7em" />
          <SevenSeg />
        </Box>
      </Box>
    </Box>
    <code>Raw out: {outval.toString(2).padStart(8, "0")}</code>
  </Box>
}
